import { prefix } from "../../design-tokens/meta";
import {
    actions,
    createStore,
    dispatchEvent,
    events,
    reducers,
    renderState,
} from "../../js/state-machine";
import { createCSSClass, isHTMLElement, isIE } from "../../js/utilities";
import PopReducer, { alignments } from "./pop-reducer";

export const componentName = "Pop";

export const defaultProps = {
    mainClass: `${prefix}pop`,
    position: actions.types.bottom,
    alignment: alignments.start,
    closeOnBlur: true,
    closeOnResize: true,
    eventName: componentName,
    ariaHaspopup: "true",
};

export const popClasses = {
    pop: defaultProps.mainClass,
    container: `${defaultProps.mainClass}__container`,
    containerHidden: `${defaultProps.mainClass}__container--hidden`,
};

export const views = {
    // The a11y says that is a better approach to remove the aria-expanded while it is false
    // renderState remove attributes so this piece is doing manually
    trigger: {
        default: { attrs: { "aria-expanded": null } },
        expanded: { attrs: { "aria-expanded": "true" } },
    },
    container: {
        opened: {
            attrs: { "aria-hidden": "false" },
            classList: [`!${popClasses.containerHidden}`],
        },
        positioning: {
            classList: [popClasses.containerHidden],
        },
        closed: {
            attrs: { "aria-hidden": "true" },
            classList: [`!${popClasses.containerHidden}`],
        },
    },
};

/**
 * Creates Pop
 * @param {HTMLElement} node
 * @returns Pop
 */
export function Pop(element, props) {
    if (!isHTMLElement(element)) return console.error(`${componentName} missing element`);
    if (element[componentName]) return console.warn(`${componentName} already initialized`);

    // Props
    props = Object.assign({}, defaultProps, props);
    const { closeOnBlur, closeOnResize } = props;

    const popInnerClasses = {
        pop: props.mainClass,
        container: `${props.mainClass}__container`,
        containerHidden: `${props.mainClass}__container--hidden`,
    };

    const popInnerViews = {
        trigger: {
            default: { attrs: { "aria-expanded": null } },
            expanded: { attrs: { "aria-expanded": "true" } },
        },
        container: {
            opened: {
                attrs: { "aria-hidden": "false" },
                classList: [`!${popInnerClasses.containerHidden}`],
            },
            positioning: {
                classList: [popInnerClasses.containerHidden],
            },
            closed: {
                attrs: { "aria-hidden": "true" },
                classList: [`!${popInnerClasses.containerHidden}`],
            },
        },
    };

    // Variables
    let containerElement = null;
    let containerDynamicClass;
    let containerDynamicClassMemory;
    let triggerElement = null;

    // Validate Marking
    const validateMarkup = () => {
        let valid = true;
        triggerElement = element.querySelector(element.dataset.trigger);
        if (!triggerElement) {
            console.error(`${componentName} missing data-trigger attribute or trigger element"`);
            valid = false;
        }

        containerElement = element.querySelector(`.${popInnerClasses.container}`);
        if (!containerElement) {
            console.error(`${componentName} missing ${popInnerClasses.container} element"`);
            valid = false;
        }

        return valid;
    };

    const closeOnTargetOutside = (target) => {
        if (isIE && !target) target = document.activeElement;

        if (
            visibilityStore.getState() &&
            !triggerElement.contains(target) &&
            !containerElement.contains(target)
        )
            close();
    };

    /**
     * In order to closeOnBlur works, both elements, trigger and the container should be focusable
     * because e.relatedTarget only recognize elements that have the focus hability
     */
    const handleTriggerBlur = (e) => {
        if (closeOnBlur) closeOnTargetOutside(e.relatedTarget);
    };

    const handleContainerBlur = (e) => {
        if (closeOnBlur) closeOnTargetOutside(e.relatedTarget);
    };

    const handleWindowResize = () => {
        if (closeOnResize && visibilityStore.getState()) close();
    };

    const handlePositioning = () => {
        renderState(popInnerViews.container.positioning, containerElement);

        // Html attr over default props
        const position = props.position.toUpperCase();
        const alignment = props.alignment.toUpperCase();

        if (!Object.values(actions.types).includes(position)) {
            console.warn("Unexpected position");
            return;
        }

        if (!Object.values(alignments).includes(alignment)) {
            console.warn("Unexpected alignment");
            return;
        }

        popStore.dispatch({
            type: position,
            alignment,
            trigger: triggerElement,
            pop: containerElement,
        });
    };

    const updateVisibility = (isVisible) => {
        renderState(
            isVisible ? popInnerViews.trigger.expanded : popInnerViews.trigger.default,
            triggerElement
        );
        renderState(
            isVisible ? popInnerViews.container.opened : popInnerViews.container.closed,
            containerElement
        );
    };

    const updatePosition = (newPosition) => {
        containerDynamicClass = `${prefix}dynamic-pop-t${newPosition.top}-r${newPosition.right}-b${newPosition.bottom}-l${newPosition.left}`;

        if (createCSSClass(`.${containerDynamicClass}`, newPosition)) {
            renderState(
                {
                    classList: [
                        containerDynamicClass,
                        containerDynamicClassMemory !== containerDynamicClass &&
                            `!${containerDynamicClassMemory}`,
                    ],
                },
                containerElement
            );
        }

        // Save last used className
        containerDynamicClassMemory = containerDynamicClass;

        return true;
    };

    const updatePop = (newPosition) => {
        updatePosition(newPosition);
    };

    const open = () => {
        if (visibilityStore.getState()) {
            renderState({ classList: [`!${containerDynamicClassMemory}`] }, containerElement);
        } else {
            handlePositioning();
            visibilityStore.dispatch(actions.open());
            dispatchEvent(events.open(props.eventName), element);
        }
    };

    const close = () => {
        if (visibilityStore.getState()) {
            visibilityStore.dispatch(actions.close());
            dispatchEvent(events.close(props.eventName), element);
        }
    };

    const toggle = () => {
        if (visibilityStore.getState()) {
            renderState({ classList: [`!${containerDynamicClassMemory}`] }, containerElement);
            close();
        } else {
            handlePositioning();
            open();
        }
    };

    // Update props
    const update = (newProps) => {
        props = Object.assign({}, props, newProps);
    };

    // Dispose
    const dispose = () => {
        element[componentName] = null;
        element = null;
        // Remove event listeners
        triggerElement.removeEventListener("click", toggle);
        closeOnBlur && triggerElement.removeEventListener("blur", handleTriggerBlur, true);
        closeOnBlur && containerElement.removeEventListener("blur", handleContainerBlur, true);
        closeOnResize && window.removeEventListener("resize", handleWindowResize, true);
    };

    // Initialization
    // Validate Markup
    if (!validateMarkup()) return false;

    // Create the store
    const visibilityStore = createStore(reducers.boolReducer, false);
    visibilityStore.subscribe(updateVisibility);

    const popStore = createStore(PopReducer, {});
    popStore.subscribe(updatePop);

    // Add initial accessibility requirements to the trigger
    triggerElement.setAttribute("aria-haspopup", props.ariaHaspopup);

    // Add event listeners
    triggerElement.addEventListener("click", toggle);
    closeOnBlur && triggerElement.addEventListener("blur", handleTriggerBlur);
    closeOnBlur && containerElement.addEventListener("blur", handleContainerBlur, true);
    closeOnResize && window.addEventListener("resize", handleWindowResize, true);

    // Api
    const api = {
        dispose,
        update,
        open,
        close,
        toggle,
        visibility: visibilityStore,
    };

    // Assignment
    element[componentName] = api;

    return api;
}
